import React, { useRef, useEffect, useState, useCallback } from 'react';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
import { TranslationKey, TranslationString } from '../../services/translationService';
import { setContentWithHighlightedPlaceholders, extractPlaceholders } from '../../utils/placeholderUtils';
import './EditableTable.css';
import { usePermissions } from '../../lib/permissions';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useNotification } from '../../context/NotificationContext';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { animateTranslation } from '../../utils/translationAnimation';
import { aiTranslationService } from '../../services/translationAI';
import { useSubscription } from '../../hooks/useSubscription';
import SubscriptionDialog from '../SubscriptionDialog/SubscriptionDialog';
import EditableCell from './EditableCell';

interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

interface Column {
  header: string;
  key: string;
  editable?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onHeaderSelect?: () => void;
}

interface EditableTableProps {
  columns: Column[];
  data: TranslationData[];
  getRowKey: (row: TranslationData) => string;
  getCellContent: (row: TranslationData, columnKey: string) => string;
  onCellChange?: (rowKey: string, columnKey: string, value: string) => void;
  onDelete?: (rowKey: string) => void;
  selectedRows?: Set<string>;
  onRowSelect?: (rowKey: string, selected: boolean) => void;
  onCellFocus?: (rowKey: string, columnKey: string, content: string, sourceLanguage: string | null) => void;
  onCellBlur?: () => void;
  onTranslate?: (rowKey: string, columnKey: string, value: string) => void;
  targetLanguage?: string | null;
  sourceLanguage?: string | null;
  onTranslateCell?: (rowKey: string, columnKey: string, sourceText: string) => Promise<{ result: string } | null>;
  getCellActions?: (row: TranslationData, columnKey: string) => { label: string; onClick: () => void }[] | undefined;
  translatingCells?: Set<string>;
  onTabNavigation?: (direction: 'next' | 'previous', rowIndex: number, columnIndex: number) => void;
}

const EditableTable: React.FC<EditableTableProps> = React.memo(({
  columns,
  data,
  getRowKey,
  getCellContent,
  onCellChange,
  onDelete,
  selectedRows = new Set(),
  onRowSelect,
  onCellFocus,
  onCellBlur,
  onTranslate,
  targetLanguage,
  sourceLanguage,
  onTranslateCell,
  getCellActions,
  translatingCells = new Set(),
  onTabNavigation,
}) => {
  const { selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { permissions } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const { showNotification } = useNotification();
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();
  const cellRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const tableRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  
  // Removed focusedCell state to reduce unnecessary re-renders

  // Store temporary value when focusing
  const tempValue = useRef<string>('');
  const [hasChanges, setHasChanges] = useState<{[key: string]: boolean}>({});

  // State for tracking the last focused cell
  const [, setLastFocusedCell] = useState<{
    rowKey: string;
    columnKey: string;
  } | null>(null);

  // Store original cell values when component mounts or data changes
  const originalDataValues = useRef<{[key: string]: {[columnKey: string]: string}}>({});
  
  // Store current data and functions in refs to avoid callback dependencies
  const currentData = useRef(data);
  const currentGetRowKey = useRef(getRowKey);
  const currentGetCellContent = useRef(getCellContent);
  
  // Update refs when props change
  useEffect(() => {
    currentData.current = data;
    currentGetRowKey.current = getRowKey;
    currentGetCellContent.current = getCellContent;
  }, [data, getRowKey, getCellContent]);
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBlurCell, setPendingBlurCell] = useState<{
    rowKey: string;
    columnKey: string;
    contentElement: HTMLElement;
    currentValue: string;
    originalContent: string;
  } | null>(null);
  
  // --- NEW STATE for managing the cell currently being edited --- 
  const [editingCell, setEditingCell] = useState<{
    rowKey: string;
    columnKey: string;
    value: string;
  } | null>(null);

  // Track recently saved cells to prevent ref callback from overriding them
  const recentlySavedCells = useRef<Set<string>>(new Set());

  // Removed focusFromTab state to reduce unnecessary re-renders

  // Helper function to get the next cell in tab order
  const getNextCellInTabOrder = (currentRowIndex: number, currentColumnIndex: number, isShiftKey: boolean = false): { rowIndex: number; columnIndex: number } | null => {
    const totalRows = data.length;

    // Get editable column indices (skip key column)
    const editableColumnIndices = columns
      .map((col, index) => ({ col, index }))
      .filter(({ col }) => col.editable !== false)
      .map(({ index }) => index);
    
    if (editableColumnIndices.length === 0) {
      return null;
    }
    
    // Find current position in editable columns
    const currentEditableIndex = editableColumnIndices.indexOf(currentColumnIndex);
    
    if (isShiftKey) {
      // Tab backwards
      if (currentEditableIndex > 0) {
        // Move to previous editable column in same row
        return { rowIndex: currentRowIndex, columnIndex: editableColumnIndices[currentEditableIndex - 1] };
      } else if (currentRowIndex > 0) {
        // Move to last editable column of previous row
        return { rowIndex: currentRowIndex - 1, columnIndex: editableColumnIndices[editableColumnIndices.length - 1] };
      }
      // At the beginning, notify parent for cross-section navigation
      if (onTabNavigation) {
        onTabNavigation('previous', currentRowIndex, currentColumnIndex);
      }
      return null;
    } else {
      // Tab forwards
      if (currentEditableIndex < editableColumnIndices.length - 1) {
        // Move to next editable column in same row
        return { rowIndex: currentRowIndex, columnIndex: editableColumnIndices[currentEditableIndex + 1] };
      } else if (currentRowIndex < totalRows - 1) {
        // Move to first editable column of next row
        return { rowIndex: currentRowIndex + 1, columnIndex: editableColumnIndices[0] };
      }
      // At the end, notify parent for cross-section navigation
      if (onTabNavigation) {
        onTabNavigation('next', currentRowIndex, currentColumnIndex);
      }
      return null;
    }
  };

  // Helper function to focus the next cell in tab order
  const focusNextCell = (currentRowIndex: number, currentColumnIndex: number, isShiftKey: boolean = false) => {
    const nextCell = getNextCellInTabOrder(currentRowIndex, currentColumnIndex, isShiftKey);
    
    if (nextCell) {
      const nextRow = data[nextCell.rowIndex];
      const nextRowKey = getRowKey(nextRow);
      const nextColumnKey = columns[nextCell.columnIndex].key;
      const nextCellKey = `${nextRowKey}-${nextColumnKey}`;
      
      // Focus next cell in tab order
      
      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        // Find the cell element and focus it
        const nextCellElement = cellRefs.current[nextCellKey];
        if (nextCellElement) {
          const contentElement = nextCellElement.querySelector('.cell-content') as HTMLElement;
          if (contentElement && contentElement.contentEditable === 'true') {
            contentElement.focus();
          }
        }
      }, 10);
    } else {
      // No next cell in this section, let parent handle cross-section navigation
      // This will trigger the onTabNavigation callback
    }
  };
  
  // Update originalDataValues when data changes
  useEffect(() => {
    const newValues: {[key: string]: {[columnKey: string]: string}} = {};
    data.forEach(row => {
      const rowKey = getRowKey(row);
      newValues[rowKey] = {};
      columns.forEach(column => {
        newValues[rowKey][column.key] = getCellContent(row, column.key);
      });
    });
    originalDataValues.current = newValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRowKey/getCellContent from refs
  }, [data, columns]);

  // Handle clicks on the document
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const grid = gridRef.current;
      
      // If we clicked outside the grid, check for unsaved changes
      if (grid && !grid.contains(target) && 
          !target.closest('.selection-action-bar') && 
          !target.closest('.translation-actions') && 
          !target.closest('.icon-text-button')) {
        
        // Find any active cell with unsaved changes
        const activeCell = document.activeElement?.closest('.editable-table-cell');
        if (activeCell) {
          const rowKey = activeCell.getAttribute('data-row');
          const columnKey = activeCell.getAttribute('data-col');
          
          if (rowKey && hasChanges[rowKey]) {
            const contentElement = activeCell.querySelector('.cell-content') as HTMLElement;
            if (contentElement) {
              const currentValue = contentElement.textContent || '';
              const row = data.find(r => getRowKey(r) === rowKey);
              if (row) {
                const originalContent = getCellContent(row, columnKey || '');
                
                if (currentValue !== originalContent) {
                  // Store the cell info for the confirmation dialog
                  setPendingBlurCell({
                    rowKey,
                    columnKey: columnKey || '',
                    contentElement,
                    currentValue,
                    originalContent
                  });
                  setShowConfirmDialog(true);
                  return;
                }
              }
            }
          }
        }
        
        // Blur any active cell
        const activeElement = document.activeElement;
        if (activeElement && grid.contains(activeElement)) {
          (activeElement as HTMLElement).blur();
          // Notify parent that cell is blurred
          onCellBlur?.();
        }
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getRowKey/getCellContent from refs
  }, [onCellBlur, hasChanges, data]);

  // Handle confirmation dialog actions
  const handleConfirmSave = () => {
    if (pendingBlurCell) {
      const { rowKey, columnKey, currentValue } = pendingBlurCell;
      onCellChange?.(rowKey, columnKey, currentValue);
      tempValue.current = currentValue;
      setHasChanges(prev => ({ ...prev, [rowKey]: false }));
    }
    setShowConfirmDialog(false);
    setPendingBlurCell(null);
  };

  const handleConfirmCancel = () => {
    if (pendingBlurCell) {
      const { contentElement, originalContent } = pendingBlurCell;
      contentElement.textContent = originalContent;
      setContentWithHighlightedPlaceholders(contentElement, originalContent);
      setHasChanges(prev => ({ ...prev, [pendingBlurCell.rowKey]: false }));
    }
    setShowConfirmDialog(false);
    setPendingBlurCell(null);
  };

  // Create stable callback functions for cell props
  const handleCellFocus = useCallback((rowKey: string, columnKey: string) => {
    // Prevent focus loops by checking if we're already focused on this cell
    setLastFocusedCell(prev => {
      if (prev?.rowKey === rowKey && prev?.columnKey === columnKey) {
        return prev; // No change needed
      }
      return { rowKey, columnKey };
    });

    // --- Get initial content reliably from props --- 
    const row = currentData.current.find(r => currentGetRowKey.current(r) === rowKey);
    const initialContent = row ? currentGetCellContent.current(row, columnKey) : '';
    
    // Store the reliable initial content
    tempValue.current = initialContent;
    setHasChanges(prev => ({ ...prev, [rowKey]: false }));
    
    // --- Set the NEW editingCell state using reliable initial content --- 
    setEditingCell({ rowKey, columnKey, value: initialContent });
    
    // Place cursor at the end of the input for better UX
    const cellKey = `${rowKey}-${columnKey}`;
    const cellElement = cellRefs.current[cellKey];
    if (cellElement) {
      const contentElement = cellElement.querySelector('.cell-content') as HTMLElement;
      if (contentElement && contentElement.contentEditable === 'true') {
        // Use a small delay to ensure the element is focused
        setTimeout(() => {
          const range = document.createRange();
          const selection = window.getSelection();
          if (selection && contentElement.childNodes.length > 0) {
            // Find the last text node
            const lastNode = contentElement.lastChild;
            if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
              range.setStart(lastNode, lastNode.textContent?.length || 0);
              range.setEnd(lastNode, lastNode.textContent?.length || 0);
            } else {
              // If no text node, place at the end of the content
              range.selectNodeContents(contentElement);
              range.collapse(false);
            }
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 10);
      }
    }
    
    // Call the parent's onCellFocus if provided 
    // (Pass the same reliable initial content)
     if (onCellFocus) {
       let sourceLanguage: string | null = null;
       if (columnKey !== 'key' && columns.length > 1) {
         for (let i = 0; i < columns.length; i++) {
           if (columns[i].key !== 'key' && columns[i].key !== columnKey) {
             sourceLanguage = columns[i].key;
             break;
           }
         }
       }
       // No need to find row/content again, we already have initialContent
       onCellFocus(rowKey, columnKey, initialContent, sourceLanguage);
     }
  }, [onCellFocus, columns]); // Removed data, getRowKey, getCellContent dependencies

    // Handle cell blur with debounce to prevent rapid focus/blur cycles
  const handleCellBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Debounce blur events to prevent rapid focus/blur cycles
    const timeoutId = setTimeout(() => {
      const blurredElement = e.currentTarget;
      
      // Early return if no blurred element
      if (!blurredElement) {
        return;
      }
      
      const currentEditingCell = editingCell; // Capture state at time of blur

      // Don't handle blur if we're in the process of translating or saving
      if (window.sessionStorage.getItem('texta_translating') || 
          window.sessionStorage.getItem('texta_saving')) {
        return;
      }

      if (currentEditingCell && blurredElement && 
          currentEditingCell.rowKey === blurredElement.getAttribute('data-row') && 
          currentEditingCell.columnKey === blurredElement.getAttribute('data-col')) {
        
        // Save changes IF they exist (using hasChanges flag)
        if (hasChanges[currentEditingCell.rowKey]) {
          let valueToSave = currentEditingCell.value;
          valueToSave = valueToSave.replace(/\n+$/, '');
          onCellChange?.(currentEditingCell.rowKey, currentEditingCell.columnKey, valueToSave);
        }
        
        // Reset states on blur
        setHasChanges(prev => ({
          ...prev,
          [currentEditingCell.rowKey]: false
        }));
        tempValue.current = '';
        setEditingCell(null); // Clear editing state
      }
      
      // Call the parent's onCellBlur callback if provided
      if (onCellBlur && blurredElement) {
        onCellBlur();
      }

      // Visual state restoration is handled by the ref callback now
    }, 50); // 50ms debounce

    // Cleanup timeout on unmount
    return () => clearTimeout(timeoutId);
  }, [onCellBlur, editingCell, hasChanges, onCellChange]);

  // Reserved for future keyboard-driven cell change
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCellChangeKeyboard = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const rowKey = target.getAttribute('data-row');
    const columnKey = target.getAttribute('data-col');
    
    if (rowKey && columnKey) {
      const currentValue = target.textContent || '';
      setHasChanges(prev => ({
        ...prev,
        [rowKey]: currentValue !== tempValue.current
      }));
    }
  }, []);

  // Helper to get source text for a specific row
  const getSourceTextForRow = useCallback((rowKey: string): string => {
    if (!sourceLanguage) return '';
    
    const row = currentData.current.find(row => currentGetRowKey.current(row) === rowKey);
    if (!row) return '';
    
    return currentGetCellContent.current(row, sourceLanguage);
  }, [sourceLanguage]); // Only depend on sourceLanguage

  // Handle cell translation
  const handleTranslateCell = useCallback(async (rowKey: string, columnKey: string, sourceText: string) => {
    // If parent provided onTranslateCell, use it (it should have subscription check)
    if (onTranslateCell) {
      return onTranslateCell(rowKey, columnKey, sourceText);
    }

    // Otherwise use internal handler with subscription check
    if (!isSubscriptionLoading && !hasActiveSubscription) {
      setIsSubscriptionDialogOpen(true);
      return null;
    }

    try {
      const translatedText = await aiTranslationService.translateText(sourceText, columnKey);
      
      if (!translatedText) return null;

      // Find the cell element
      const cellElement = document.querySelector(`.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`);
      if (!cellElement) {
        return null;
      }

      // Animate the translation
      await animateTranslation({
        cellElement: cellElement as HTMLElement,
        translatedText,
        onComplete: () => {
          // Update the data model
          onCellChange?.(rowKey, columnKey, translatedText);
        }
      });

      return { result: translatedText };
    } catch (error) {
      console.error('Error translating cell:', error);
      showNotification('Failed to translate');
      return null;
    }
  }, [onTranslateCell, onCellChange, showNotification, isSubscriptionLoading, hasActiveSubscription]);

  // Function to check if a cell is missing placeholders
  const hasMissingPlaceholders = useCallback((rowKey: string, columnKey: string): boolean => {
    // Skip the key column
    if (columnKey === 'key') return false;
    
    // Get the first non-key column (second column)
    const firstLangColumn = columns.find(col => col.key !== 'key');
    if (!firstLangColumn) return false;
    
    // Only show error on the third column (target language)
    if (columnKey === firstLangColumn.key) return false;
    
    // Find the row data
    const row = currentData.current.find(row => currentGetRowKey.current(row) === rowKey);
    if (!row) return false;
    
    // Get first language text from the row's strings (second column)
    const firstLangString = row.strings.find(s => s.language_code === firstLangColumn.key);
    if (!firstLangString) return false;
    
    // Get current text from the row's strings (third column)
    const currentString = row.strings.find(s => s.language_code === columnKey);
    if (!currentString || !currentString.value.trim()) return false;
    
    // Extract placeholders from both texts
    const firstLangPlaceholders = extractPlaceholders(firstLangString.value);
    const currentPlaceholders = extractPlaceholders(currentString.value);
    
    // Check if any placeholders from first language are missing in current text
    // Also check if any placeholders in current text are not in first language
    return firstLangPlaceholders.some(placeholder => !currentPlaceholders.includes(placeholder)) ||
           currentPlaceholders.some(placeholder => !firstLangPlaceholders.includes(placeholder));
  }, [columns]); // Only depend on columns

  // Shared function to save cell changes (used by both Enter key and Save button)
  const saveCellChanges = useCallback((rowKey: string, columnKey: string, columnIndex: number, valueToSave: string) => {
    // Set saving flag
    window.sessionStorage.setItem('texta_saving', 'true');
    
    // Mark this cell as recently saved to prevent ref callback from overriding
    const cellKey = `${rowKey}-${columnKey}`;
    recentlySavedCells.current.add(cellKey);
    
    // Update hasChanges state first to prevent reversion
    setHasChanges(prev => ({ ...prev, [rowKey]: false }));
    
    // Save changes
    onCellChange?.(rowKey, columnKey, valueToSave);
    
    // Update temp value (maybe not needed?)
    tempValue.current = valueToSave;
    
    // Clear editing state AFTER saving
    setEditingCell(null);
    
    // Clear the flag after a short delay
    setTimeout(() => {
      window.sessionStorage.removeItem('texta_saving');
    }, 100);
    
    // Remove from recently saved after a longer delay
    setTimeout(() => {
      recentlySavedCells.current.delete(cellKey);
    }, 2000);
  }, [onCellChange]);

  // Handle save button click
  const handleSaveClick = useCallback((e: React.MouseEvent<HTMLButtonElement>, rowKey: string, columnKey: string, columnIndex: number) => {
    e.stopPropagation(); // Prevent blur event
    const cell = e.currentTarget.closest('.editable-table-cell');
    // --- Use editingCell.value for saving --- 
    if (cell && editingCell && editingCell.rowKey === rowKey && editingCell.columnKey === columnKey && editingCell.value !== undefined) {
      let valueToSave = editingCell.value;
      // --- Remove ALL trailing newlines --- 
      valueToSave = valueToSave.replace(/\n+$/, ''); 
      
      // Force blur to exit edit mode
      const contentElement = cell.querySelector('.cell-content') as HTMLElement;
      if (contentElement) {
        contentElement.blur();
      }
      
      // Use the shared save function
      saveCellChanges(rowKey, columnKey, columnIndex, valueToSave);
    } else {
       
    }
  }, [editingCell, saveCellChanges]);

  // Determine if this is a two-column layout (key + source language only)
  const isTwoColumnLayout = columns.length === 2;





  // Create stable callbacks for inline functions in cell rendering
  const createCellInputHandler = useCallback((rowKey: string, columnKey: string, columnIndex: number) => {
    return (e: React.FormEvent<HTMLDivElement>) => {
      if (columnIndex !== 0) {
        const target = e.target as HTMLElement;
        const currentValue = target.textContent || '';
        
        // Only update state if the value actually changed
        setEditingCell(prev => {
          if (prev && prev.rowKey === rowKey && prev.columnKey === columnKey) {
            if (prev.value !== currentValue) {
              return { ...prev, value: currentValue };
            }
            return prev;
          }
          return prev;
        });
        
        // Only update hasChanges if it actually changed
        const hasChanged = currentValue !== tempValue.current;
        setHasChanges(prev => {
          if (prev[rowKey] !== hasChanged) {
            return { ...prev, [rowKey]: hasChanged };
          }
          return prev;
        });
      }
    };
  }, []); // Empty dependency array - stable factory function

  const createCellKeyDownHandler = useCallback((rowKey: string, columnKey: string, columnIndex: number, rowIndex: number) => {
    return (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (columnIndex === 0) {
        // Prevent any keyboard events on the first column
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Handle Tab key for custom navigation
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        
        // Save current changes if any
        if (editingCell && editingCell.rowKey === rowKey && editingCell.columnKey === columnKey) {
          let valueToSave = editingCell.value;
          valueToSave = valueToSave.replace(/\n+$/, '');
          
          window.sessionStorage.setItem('texta_saving', 'true');
          setHasChanges(prev => ({ ...prev, [rowKey]: false }));
          onCellChange?.(rowKey, columnKey, valueToSave);
          tempValue.current = valueToSave;
          setEditingCell(null);
          
          setTimeout(() => {
            window.sessionStorage.removeItem('texta_saving');
          }, 100);
        }
        
        // Focus next cell in tab order
        focusNextCell(rowIndex, columnIndex, e.shiftKey);
        return;
      }

      // Handle Enter key
      if (e.key === 'Enter') {
        if (!e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();

          // --- Use editingCell.value for saving --- 
          if (editingCell && editingCell.rowKey === rowKey && editingCell.columnKey === columnKey && editingCell.value !== undefined) {
            let valueToSave = editingCell.value;
            // --- Remove ALL trailing newlines --- 
            valueToSave = valueToSave.replace(/\n+$/, ''); 
            
            // Force blur to exit edit mode
            e.currentTarget.blur();
            
            // Use the shared save function
            saveCellChanges(rowKey, columnKey, columnIndex, valueToSave);
          }
        } 
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focusNextCell is stable
  }, [editingCell, onCellChange, saveCellChanges]);

  const createCellRefHandler = useCallback((cellKey: string, rowKey: string, columnKey: string, columnIndex: number) => {
    return (el: HTMLDivElement | null) => {
      if (el) {
        cellRefs.current[cellKey] = el;
      }
    };
  }, []); // Empty dependency array - stable factory function

  const createContentRefHandler = useCallback((cellKey: string, rowKey: string, columnKey: string, columnIndex: number, content: string) => {
    return (el: HTMLElement | null) => {
      if (el) {
        const isCurrentlyEditingThisCell = 
          editingCell?.rowKey === rowKey && 
          editingCell?.columnKey === columnKey;
        
        const isRecentlySaved = recentlySavedCells.current.has(cellKey);

        // Only modify display if the element is NOT the one being edited AND not recently saved
        if (!isCurrentlyEditingThisCell && !isRecentlySaved) {
          // Check if the current content is different from what we want to set
          const currentContent = columnIndex === 0 ? el.textContent : el.innerHTML;
          const shouldUpdate = columnIndex === 0 
            ? currentContent !== content
            : !el.innerHTML.includes(content); // For translation columns, check if content is present
          
          if (shouldUpdate) {
            // Apply visual state *only* for non-key columns when not focused
            if (columnIndex === 0) {
              // Key column (not editing): just set raw text content
              el.textContent = content;
            } else {
              // Translation column (not editing): apply visual state (hidden chars, placeholders)
              setContentWithHighlightedPlaceholders(el, content);
            }
          }
        } else {
          // Element is being edited or recently saved: Do nothing here. 
          // Let contentEditable and onInput handler manage the display.
        }
      }
    };
  }, [editingCell]); // Keep editingCell dependency for now



  return (
    <div className={`editable-table ${isTwoColumnLayout ? 'two-column' : ''}`} ref={tableRef}>
      
      {/* Headers Row */}
      <div className="editable-table-headers">
        {columns.map((column) => (
          <div key={column.key} className="editable-table-header">
            {column.isSelectable && (
              <input
                type="checkbox"
                className="header-checkbox"
                checked={column.isSelected}
                onChange={column.onHeaderSelect}
              />
            )}
            <img src={dropdownIcon} alt="Dropdown" className="header-icon" />
            <span>{column.header}</span>
          </div>
        ))}
      </div>

      {/* Table Body with Rows */}
      <div className="editable-table-body" ref={gridRef}>
        {data.map((row, rowIndex) => {
          const rowKey = getRowKey(row);
          const isRowSelected = selectedRows.has(rowKey);
          const isRowTranslating = translatingCells.has(rowKey);
          
          return (
            <div 
              key={rowKey}
              className={`editable-table-row ${isRowSelected ? 'selected' : ''} ${isRowTranslating ? 'translating' : ''}`}
              data-row-key={rowKey}
              data-row-index={rowIndex}
            >
              {columns.map((column, columnIndex) => {
                const cellKey = `${rowKey}-${column.key}`;
                
                const content = getCellContent(row, column.key);
                const isCellEditable = column.editable !== false;
                const isTargetLang = column.key === targetLanguage;
                const hasSourceText = isTargetLang && getSourceTextForRow(rowKey).trim() !== '';
                const isMissingPlaceholders = hasMissingPlaceholders(rowKey, column.key);
                const isTranslating = translatingCells.has(cellKey);
                const isEditing = editingCell?.rowKey === rowKey && editingCell?.columnKey === column.key;
                const editingValue = editingCell?.value || '';
                
                return (
                  <EditableCell
                    key={cellKey}
                    rowKey={rowKey}
                    columnKey={column.key}
                    columnIndex={columnIndex}
                    content={content}
                    isCellEditable={isCellEditable}
                    isTargetLang={isTargetLang}
                    hasSourceText={hasSourceText}
                    isMissingPlaceholders={isMissingPlaceholders}
                    isTranslating={isTranslating}
                    isRowSelected={isRowSelected}
                    hasChanges={hasChanges[rowKey] || false}
                    isEditing={isEditing}
                    editingValue={editingValue}
                    permissions={permissions}
                    onRowSelect={onRowSelect}
                    onDelete={onDelete}
                    onCellFocus={handleCellFocus}
                    onCellBlur={handleCellBlur}
                    onCellInput={createCellInputHandler(rowKey, column.key, columnIndex)}
                    onCellKeyDown={createCellKeyDownHandler(rowKey, column.key, columnIndex, rowIndex)}
                    onTranslateCell={handleTranslateCell}
                    onSaveClick={handleSaveClick}
                    getSourceTextForRow={getSourceTextForRow}
                    cellRef={createCellRefHandler(cellKey, rowKey, column.key, columnIndex)}
                    contentRef={createContentRefHandler(cellKey, rowKey, column.key, columnIndex, content)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
      
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={handleConfirmCancel}
        onConfirm={handleConfirmSave}
        message="You have unsaved changes. Would you like to save them?"
        confirmText="Save"
        cancelText="Discard"
      />
      <SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
      />
    </div>
  );
}, (prevProps: EditableTableProps, nextProps: EditableTableProps) => {
  // Custom comparison function for EditableTable memoization
  return (
    prevProps.data === nextProps.data &&
    prevProps.columns === nextProps.columns &&
    prevProps.selectedRows === nextProps.selectedRows &&
    prevProps.translatingCells === nextProps.translatingCells &&
    prevProps.targetLanguage === nextProps.targetLanguage &&
    prevProps.sourceLanguage === nextProps.sourceLanguage
  );
});

export default EditableTable;